import React, { useState, useRef, useEffect } from "react";
import { Invoice } from "../../../../features/groups/groupSlice";
import { MdDownload, MdOpenInNew, MdMoreVert, MdContentCopy } from "react-icons/md";
import { toast } from "react-toastify";
import DownloadFormatModal from "./DownloadFormatModal";
import { DownloadOptions, downloadSingleInvoice } from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
interface InvoiceActionsProps {
  invoice: Invoice;
}

const InvoiceActions: React.FC<InvoiceActionsProps> = ({ invoice }) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMenuOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 200; // Approximate menu height

      // Check if there's enough space below the button
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      let top = buttonRect.bottom + 8; // Default: below the button
      let right = window.innerWidth - buttonRect.right;

      // If not enough space below, position above
      if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
        top = buttonRect.top - menuHeight - 8;
      }

      // Ensure menu doesn't go off-screen horizontally
      if (right < 0) {
        right = 8;
      }

      setMenuPosition({ top, right });
    }
  }, [isMenuOpen]);

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }
  }, [isMenuOpen]);

  const handleViewInvoice = () => {
    if (invoice.invoice_url) {
      window.open(invoice.invoice_url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Invoice URL not available");
    }
  };

  const handleDownloadInvoice = () => {
    setIsDownloadModalOpen(true);
    setIsMenuOpen(false); // Close mobile menu if open
  };

  const handleDownloadConfirm = (options: DownloadOptions) => {
    try {
      downloadSingleInvoice(invoice, options);
      toast.success(`Invoice ${invoice.invoice_number} downloaded successfully!`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download invoice. Please try again.");
    }
  };

  const handleCopyInvoiceNumber = () => {
    navigator.clipboard.writeText(invoice.invoice_number);
    toast.success("Invoice number copied to clipboard");
  };

  const handleCopyInvoiceId = () => {
    navigator.clipboard.writeText(invoice.invoice_id);
    toast.success("Invoice ID copied to clipboard");
  };

  return (
    <>
      <div className="relative">
        {/* Desktop Actions */}
        <div className="hidden sm:flex items-center space-x-2">
          <button
            onClick={handleViewInvoice}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title={language?.groupSubscription?.viewInvoice}
          >
            <MdOpenInNew className="w-4 h-4 mr-1" />
            {language?.groupSubscription?.view}
          </button>
          <button
            onClick={handleDownloadInvoice}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title={language?.groupSubscription?.download}
          >
            <MdDownload className="w-4 h-4 mr-1" />
            {language?.groupSubscription?.download}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <div className="sm:hidden relative">
          <button
            ref={buttonRef}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdMoreVert className="w-5 h-5" />
          </button>

          {isMenuOpen && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-50" onClick={() => setIsMenuOpen(false)} />

              {/* Menu - Smart positioning */}
              <div
                ref={menuRef}
                className="fixed w-48 bg-white rounded-md shadow-lg z-[60] border border-gray-200 max-h-64 overflow-y-auto"
                style={{
                  top: `${menuPosition.top}px`,
                  right: `${menuPosition.right}px`,
                }}
              >
                <div className="py-1">
                  <button
                    onClick={() => {
                      handleViewInvoice();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MdOpenInNew className="w-4 h-4 mr-3" />
                    {language?.groupSubscription?.viewInvoice}
                  </button>
                  <button
                    onClick={() => {
                      handleDownloadInvoice();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MdDownload className="w-4 h-4 mr-3" />
                    {language?.groupSubscription?.download}
                  </button>
                  <button
                    onClick={() => {
                      handleCopyInvoiceNumber();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MdContentCopy className="w-4 h-4 mr-3" />
                    {language?.groupSubscription?.copyInvoice} #
                  </button>
                  <button
                    onClick={() => {
                      handleCopyInvoiceId();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MdContentCopy className="w-4 h-4 mr-3" />
                    {language?.groupSubscription?.copyInvoiceID}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Download Format Modal */}
      <DownloadFormatModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onConfirm={handleDownloadConfirm}
        title={language?.groupSubscription?.download}
        subtitle={`${language?.groupSubscription?.chooseFormatFor} ${invoice.invoice_number}`}
        isBulk={false}
      />
    </>
  );
};

export default InvoiceActions;
